import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";

/**
 * Minimal admin UI: invite instructor/learner + assign learner.
 * Does not display invite tokens after navigation away; token shown once on create when email send is off.
 * TRAINING_ENABLED is granted only via assignment (or explicit entitlement API) — not by invite alone.
 */
export default function AdminInstructorOnboarding() {
  const [inviteEmail, setInviteEmail] = useState("");
  const [learnerInviteEmail, setLearnerInviteEmail] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [learnerInviteResult, setLearnerInviteResult] = useState(null);
  const [invites, setInvites] = useState([]);
  const [learnerInvites, setLearnerInvites] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [learners, setLearners] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [programmeId, setProgrammeId] = useState("programme-nz-road-code");
  const [instructorId, setInstructorId] = useState("");
  const [learnerId, setLearnerId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [inv, linv, ins, learn, asg] = await Promise.all([
      api.get("/admin/instructor-invites"),
      api.get("/admin/learner-invites"),
      api.get("/admin/instructors"),
      api.get("/admin/learners"),
      api.get("/admin/instructor-assignments"),
    ]);
    setInvites(inv.data.invites || []);
    setLearnerInvites(linv.data.invites || []);
    setInstructors(ins.data.instructors || []);
    setLearners(learn.data.learners || []);
    setAssignments(asg.data.assignments || []);
  };

  useEffect(() => {
    reload().catch((err) => setError(formatApiError(err)));
  }, []);

  const createInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    setInviteResult(null);
    try {
      const res = await api.post("/admin/instructor-invites", { email: inviteEmail });
      setInviteResult(res.data);
      setMessage("Invitation created.");
      setInviteEmail("");
      await reload();
    } catch (err) {
      setError(formatApiError(err) || "Could not create invite.");
    } finally {
      setBusy(false);
    }
  };

  const createLearnerInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    setLearnerInviteResult(null);
    try {
      const res = await api.post("/admin/learner-invites", { email: learnerInviteEmail });
      setLearnerInviteResult(res.data);
      setMessage("Learner invitation created (PUBLIC_BASIC until assigned).");
      setLearnerInviteEmail("");
      await reload();
    } catch (err) {
      setError(formatApiError(err) || "Could not create learner invite.");
    } finally {
      setBusy(false);
    }
  };

  const assign = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api.post("/admin/instructor-assignments", {
        instructor_id: instructorId,
        learner_id: learnerId,
        programme_id: programmeId,
      });
      setMessage("Learner assigned to instructor.");
      await reload();
    } catch (err) {
      setError(formatApiError(err) || "Could not assign learner.");
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (inviteId) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api.post(`/admin/instructor-invites/${inviteId}/revoke`);
      setMessage("Invitation revoked.");
      setInviteResult(null);
      await reload();
    } catch (err) {
      setError(formatApiError(err) || "Could not revoke invite.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card max-w-3xl space-y-8" data-testid="admin-instructor-onboarding">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-base md:text-lg font-semibold">Instructor onboarding</h1>
        <Link to="/admin" className="text-sm underline" data-testid="admin-onboarding-back">
          Back to admin
        </Link>
      </div>

      {error && (
        <p role="alert" className="text-destructive text-sm" data-testid="admin-onboarding-error">
          {error}
        </p>
      )}
      {message && (
        <p className="text-sm" data-testid="admin-onboarding-message">
          {message}
        </p>
      )}

      <section className="space-y-3" data-testid="admin-invite-section">
        <h2 className="font-medium text-sm">Invite instructor</h2>
        <form onSubmit={createInvite} className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="invite-email" className="text-sm">
              Email
            </label>
            <input
              id="invite-email"
              data-testid="invite-email-input"
              className="input"
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="invite-submit">
            {busy ? "Working…" : "Create invitation"}
          </button>
        </form>
        {inviteResult?.invite_url && (
          <div className="text-xs p-2 border rounded space-y-1" data-testid="invite-handoff">
            <p>Email send is off — share this one-time link privately (not logged):</p>
            <code className="break-all">{inviteResult.invite_url}</code>
          </div>
        )}
        <ul className="text-xs space-y-1" data-testid="invite-list">
          {invites.map((inv) => (
            <li key={inv.id} className="flex flex-wrap items-center gap-2">
              <span>
                {inv.email} · {inv.status}
                {inv.used ? " · used" : ""} · expires {inv.expires_at}
              </span>
              {inv.status === "pending" && !inv.used ? (
                <button
                  type="button"
                  className="underline"
                  disabled={busy}
                  data-testid={`invite-revoke-${inv.id}`}
                  onClick={() => revokeInvite(inv.id)}
                >
                  Revoke
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3" data-testid="admin-learner-invite-section">
        <h2 className="font-medium text-sm">Invite learner</h2>
        <p className="text-xs text-muted-foreground">
          Learner sets their own password. Starts as PUBLIC_BASIC. Assign an instructor below to grant
          TRAINING_ENABLED for practical feedback.
        </p>
        <form onSubmit={createLearnerInvite} className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="learner-invite-email" className="text-sm">
              Email
            </label>
            <input
              id="learner-invite-email"
              data-testid="learner-invite-email-input"
              className="input"
              type="email"
              required
              value={learnerInviteEmail}
              onChange={(e) => setLearnerInviteEmail(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="learner-invite-submit">
            {busy ? "Working…" : "Create learner invitation"}
          </button>
        </form>
        {learnerInviteResult?.invite_url && (
          <div className="text-xs p-2 border rounded space-y-1" data-testid="learner-invite-handoff">
            <p>Email send is off — share this one-time link privately (not logged):</p>
            <code className="break-all">{learnerInviteResult.invite_url}</code>
          </div>
        )}
        <ul className="text-xs space-y-1" data-testid="learner-invite-list">
          {learnerInvites.map((inv) => (
            <li key={inv.id}>
              {inv.email} · {inv.status}
              {inv.used ? " · used" : ""} · expires {inv.expires_at}
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3" data-testid="admin-assign-section">
        <h2 className="font-medium text-sm">Assign learner to instructor</h2>
        <p className="text-xs text-muted-foreground">
          Assignment grants TRAINING_ENABLED for the learner (approved path — not a global flag).
        </p>
        <form onSubmit={assign} className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="assign-instructor" className="text-sm">
              Instructor
            </label>
            <select
              id="assign-instructor"
              className="input"
              data-testid="assign-instructor-select"
              required
              value={instructorId}
              onChange={(e) => setInstructorId(e.target.value)}
            >
              <option value="">Select…</option>
              {instructors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.display_name || i.email}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="assign-learner" className="text-sm">
              Learner
            </label>
            <select
              id="assign-learner"
              className="input"
              data-testid="assign-learner-select"
              required
              value={learnerId}
              onChange={(e) => setLearnerId(e.target.value)}
            >
              <option value="">Select…</option>
              {learners.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.display_name || l.email}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="assign-programme" className="text-sm">
              Programme ID
            </label>
            <input
              id="assign-programme"
              className="input"
              data-testid="assign-programme-input"
              required
              value={programmeId}
              onChange={(e) => setProgrammeId(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="assign-submit">
            {busy ? "Working…" : "Assign"}
          </button>
        </form>
        <ul className="text-xs space-y-1" data-testid="assignment-list">
          {assignments.map((a) => (
            <li key={a.id}>
              instructor {a.instructor_id.slice(0, 8)}… → learner {a.learner_id.slice(0, 8)}… · {a.programme_id}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
